import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../Controllers/hooks";

import { Toast } from "primereact/toast";
import { Ripple } from "primereact/ripple";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { OverlayPanel } from "primereact/overlaypanel";

import PageLoader from "../../Components/PageLoader";
import NoDataCreated from "../../Components/NoDataCreated";
import ErrorPage from "../../Components/ErrorPage";
import { goToTop } from "../../Components/goToTop";

import { DefaultPackage } from "../../Controllers/Types/Project/DefaultPackageInterface";
import { Project } from "../../Controllers/Types/Project/ProjectInterface";
import { fetchServiceProjects } from "../../Controllers/Thunks/layoutThunks";
import GoBackButton from "../../Components/GoBackButton";
import { Service } from "../../Controllers/Types/Project/Service/ServiceInterface";
import { ShareContact } from "../../Controllers/Types/Project/ShareContactInterface";

const ServiceProjects: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const toast = useRef<Toast>(null);
    const projectInfoPanel = useRef<OverlayPanel>(null);

    const {
        loadingServiceProjects,
        errorServiceProjects,
        serviceProjects
    } = useAppSelector((state) => state?.layout);

    const currentService: DefaultPackage = location.state.service;

    const tabs = ["All", "Current", "Ongoing", "Completed"] as const;
    type ProjectFilter = typeof tabs[number];
    const [projectsFilterState, setProjectsFilterState] = useState<ProjectFilter>("All");
    const [loadingViewProjectId, setLoadingViewProjectId] = useState<number | null>(null);
    const [filteredServiceProjects, setFilteredServiceProjects] = useState<Project[]>([]);
    const [projectInactiveMessage, setProjectInactiveMessage] = useState<string>('');
    const choosedActiveTab = localStorage.getItem("activeServiceProjectTab") as ProjectFilter;

    // fetch data initially
    useEffect(() => {
        if (!currentService) return;
        dispatch(fetchServiceProjects(currentService?.id));
    }, [dispatch, currentService]);

    const getProjectsCount = (type: ProjectFilter): number => {
        if (!serviceProjects) return 0;

        if (type === "All") {
            return serviceProjects.length;
        }

        return serviceProjects.filter(
            (project) => project?.status?.toLowerCase() === type?.toLowerCase()
        )?.length;
    };

    useEffect(() => {
        if (choosedActiveTab) {
            setProjectsFilterState(choosedActiveTab);
        } else {
            setProjectsFilterState('All');
        }
    }, [choosedActiveTab]);

    useEffect(() => {
        if (!serviceProjects) {
            setFilteredServiceProjects([]);
            return;
        }

        const filtered =
            projectsFilterState === "All"
                ? serviceProjects
                : serviceProjects.filter(
                    (project) => project?.status === projectsFilterState
                );

        setFilteredServiceProjects(filtered);
    }, [projectsFilterState, serviceProjects]);

    const handleFilterProjects = (filterState: ProjectFilter) => {
        localStorage.setItem("activeServiceProjectTab", filterState);
        setProjectsFilterState(filterState);
        goToTop();
    };

    // for table actions
    const tableActionBody = (rowData: Project) => {
        const project: Project = rowData;

        return (
            <div className="tabel_btn_area mt-1 mb-1">
                {handleCheckIsProjectActive(project) === true ? (
                    <Button
                        key={project?.project_id}
                        icon={`bi bi-eye`}
                        label={"View"}
                        className="tabel_btn view_btn fixed_width"
                        onClick={() => {
                            // need to replace
                            goToService(project)
                        }

                        }
                        loading={loadingViewProjectId === project?.project_id}
                    />
                ) : (
                    <Button
                        key={project?.project_id}
                        icon={`ri ri-lock-fill`}
                        label={"Locked"}
                        className="tabel_btn view_btn locked fixed_width"
                        onClick={(e) => handleViewProjectInactiveInfo(e, 'The project license has expired. Please go to Projects and renew the license.')}
                    />
                )}
            </div>
        );
    };

    const handleViewProjectInactiveInfo = (e: React.MouseEvent<HTMLButtonElement>, message: string) => {
        if (projectInfoPanel.current) {
            projectInfoPanel.current.hide();
            setTimeout(() => projectInfoPanel.current?.show(e, e.currentTarget), 50);
        }

        setProjectInactiveMessage(message);
    };

    // const handleViewProject = (project: Project | null) => {
    //     if (!project) {
    //         setLoadingViewProjectId(null);

    //         toast.current?.show({
    //             severity: "error",
    //             summary: "Failed",
    //             detail: "There was an error viewing project details.",
    //             life: 3000,
    //         });

    //         return;
    //     }

    //     setLoadingViewProjectId(project.project_id);

    //     navigate("/project/detail", {
    //         state: { projectData: project },
    //     });

    //     goToTop();
    // };

    const getRowClassNameForProject = (data: Project, options: { [key: string]: any }) => {
        const projectType = data.project_type;
        const status = data.status;
        const isProjectInactive: boolean = (!handleCheckIsProjectActive(data));

        if (status === 'Current') {
            if (projectType === "CP") {
                if (isProjectInactive) {
                    return "project_data_row current locked commercial ";
                } else {
                    return "project_data_row current commercial";
                }
            } else if (projectType === "DP") {
                if (isProjectInactive) {
                    return "project_data_row current locked domestic";
                } else {
                    return "project_data_row current domestic";
                }
            }
            return "project_data_row current";
        } else if (status === 'Ongoing') {
            if (isProjectInactive) {
                return "project_data_row ongoing locked";
            } else {
                return "project_data_row ongoing";
            }
        } else if (status === 'Completed') {
            if (isProjectInactive) {
                return "project_data_row completed locked";
            } else {
                return "project_data_row completed";
            }
        }
    };

    const handleCheckIsProjectActive = (project?: Project | null): boolean => {
        return project?.this_project_active === 1;
    };

    const goToService = (  project: Project | null) => {
        const service: Service|null =project?.shortcutService!;
        console.log(service);
        const projectServiceId = service?.folder_id;
        goToTop();
        const serviceAssignedContact: ShareContact[] = service.assign;

        const itemData = {
            project_id: project?.project_id,
            job_number: project?.job_number,
            address: project?.address,
            description: project?.description,
            folder_id: service.folder_id,
            folder: service.folder,
            service_name: service.service_name,
            feature_name: service.feature_name,
            folder_type: service.folder_type,
            serviceAssignedContact: serviceAssignedContact,
            projectAllContact: project?.contact!,
            sharedBy: service.shared_by,
            filePath: service?.file_path,
            can_edit: true,
        };

     if (service.type === "Default") {
            if (service.folder === "Pre Comencencemet") {
                navigate(
                    `/project/detail/default-service/pre-commencement/${service.feature_key_name}`,
                    { state: { data: itemData, folderId: service.folder_id } }
                );
            } else if (service.folder === "Onsite") {
                // if (service.feature_key_name === 'site-visit') {
                //   handleShowInfoModal();
                // } else {
                navigate(
                    `/project/detail/default-service/onsite/${service.feature_key_name}`, {
                    state: { data: itemData, folderId: service.folder_id },
                });
                // }
            } else {
                navigate(
                    `/project/detail/default-service/general/${service.feature_key_name}`, {
                    state: { data: itemData, folderId: service.folder_id },
                });
            }

            // If the module is under development, show the info model.
            // handleShowInfoModal();
        }
    };

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <OverlayPanel ref={projectInfoPanel} className="project_info_panel" dismissable>
                <p>
                    <i className="bi bi-exclamation-circle me-2 text-warning"></i>
                    {projectInactiveMessage}
                </p>
                <Link
                    to={`/project`}
                    onClick={() => {
                        goToTop();
                        localStorage.setItem('activeProjectTab', 'all');
                        localStorage.setItem("activeProjectViewType", 'My project');
                        localStorage.setItem("activeSharedFolderCategory", 'Others');
                    }}
                >
                    Projects&nbsp;<i className="bi bi-box-arrow-up-right"></i>
                </Link>
            </OverlayPanel>

            <div className="main_content layout_padding show_side_bar">
                {loadingServiceProjects ? (
                    <PageLoader />
                ) : errorServiceProjects ? (
                    <ErrorPage errorMessage={`There was an error retrieving projects for the ${(currentService?.feature_name)?.toLowerCase()}.`} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="d-flex align-items-center justify-content-between">
                            <h4 className="main_heading">{currentService?.feature_name} <span className="text_empty">- Projects</span></h4>
                            <GoBackButton />
                        </div>

                        <div className="page_header_area mt-sm-3 mt-3">
                            <div className="contact_tabs">
                                {tabs.map((tab) => (
                                    <div key={tab} className="contact_tab_item">
                                        <button
                                            type="button"
                                            className={`contact_tab_btn ${projectsFilterState === tab ? "active" : ""} p-ripple`}
                                            onClick={() => handleFilterProjects(tab)}
                                            disabled={serviceProjects?.length === 0}
                                        >
                                            {tab}:&nbsp;{String(getProjectsCount(tab)).padStart(2, '0')}
                                            <Ripple pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" } } }} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="page_main_content_section p-0">
                            {filteredServiceProjects && filteredServiceProjects?.length > 0 ? (
                                <DataTable
                                    value={filteredServiceProjects}
                                    paginator
                                    size="small"
                                    rows={10}
                                    emptyMessage="No project(s) found!"
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    tableStyle={{ minWidth: "50rem" }}
                                    paginatorDropdownAppendTo={"self"}
                                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                                    className="page_table p-0 p-sm-2"
                                    rowHover
                                    scrollable
                                    rowClassName={getRowClassNameForProject}
                                    dataKey="project_id"
                                >
                                    <Column
                                        header="Job no."
                                        headerClassName="text_purple"
                                        field="job_number"
                                        body={(rowData: Project) => (
                                            <span className="text-capitalize text_bold">
                                                {rowData?.job_number ? rowData?.job_number : "---"}
                                            </span>
                                        )}
                                        style={{ width: "15%" }}
                                    ></Column>

                                    <Column
                                        header="Type"
                                        headerClassName="text_purple"
                                        field="project_type"
                                        body={(rowData: Project) => (
                                            <span className="text-capitalize">
                                                {rowData?.project_type === "CP" ? "Commercial" : "Domestic"}
                                            </span>
                                        )}
                                        style={{ width: "15%" }}
                                    ></Column>

                                    <Column
                                        header="Address"
                                        headerClassName="text_purple"
                                        field="address"
                                        body={(rowData: Project) => (
                                            <span className="text-capitalize">
                                                {rowData?.address ? rowData?.address : "---"}
                                            </span>
                                        )}
                                        style={{ width: "25%" }}
                                    ></Column>

                                    <Column
                                        header="Description"
                                        headerClassName="text_purple"
                                        field="description"
                                        className="first_letter_capitalized"
                                        body={(rowData: Project) => (
                                            <span>
                                                {rowData?.description ? `${rowData.description.slice(0, 60)}${rowData.description?.length > 60 ? '...' : ''}` : "---"}
                                            </span>
                                        )}
                                        style={{ width: "30%" }}
                                    ></Column>

                                    <Column
                                        body={tableActionBody}
                                        alignHeader="center"
                                        className="table_btn_area"
                                        style={{ width: "15%" }}
                                        alignFrozen="right"
                                        frozen
                                    ></Column>
                                </DataTable>
                            ) : (
                                <NoDataCreated
                                    message={
                                        `${serviceProjects?.length === 0 ? `No projects are available with ${(currentService?.feature_name)?.toLowerCase()} service.`
                                            : filteredServiceProjects?.length === 0 ? `No ${projectsFilterState !== 'All' ? (projectsFilterState)?.toLowerCase() : ''} project(s) were found.` :
                                                'No project(s) found!'}`
                                    }
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
        </React.Fragment>
    )
}

export default ServiceProjects;